import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HeaderActions } from '@/components/layout/AppShell';
import { t } from '@/i18n';
import { useMileageApi, useCompaniesApi } from '@/services/api';
import { VehicleManagement } from './VehicleManagement';
import { StandardRouteManagement } from './StandardRouteManagement';
import { MonthlyMileageView } from './MonthlyMileageView';
import { MileagePlacesPanel } from './MileagePlacesPanel';
import type { VehicleListItem, StructuredAddress } from '@ip/shared-types';

interface MileageViewProps {
  companyNip: string;
  companyName: string;
  onCompanyNameLoad?: (name: string) => void;
}

export function MileageView({ companyNip, companyName, onCompanyNameLoad }: MileageViewProps) {
  const [vehicles, setVehicles] = useState<VehicleListItem[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [activeTab, setActiveTab] = useState('vehicles');
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(() => new Set(['vehicles']));
  const [companyAddress, setCompanyAddress] = useState<StructuredAddress | undefined>();
  const { getVehicles } = useMileageApi();
  const { getCompanyDetail } = useCompaniesApi();

  useEffect(() => {
    async function load() {
      try {
        const data = await getVehicles(companyNip);
        setVehicles(data);
        if (data.length > 0 && !selectedVehicleId) {
          setSelectedVehicleId(data[0].id);
        }
        if (companyNip !== 'other') {
          const detail = await getCompanyDetail(companyNip);
          if (!companyName && onCompanyNameLoad && detail.name) {
            onCompanyNameLoad(detail.name);
          }
          // Build company address for default office location
          if (detail.city || detail.street) {
            // Parse street number from combined street field (e.g., "ul. Bielska 7" → street="ul. Bielska", number="7")
            let street = detail.street || undefined;
            let number: string | undefined;
            if (street) {
              const match = street.match(/^(.+?)\s+(\d+\S*)$/);
              if (match) {
                street = match[1];
                number = match[2];
              }
            }
            setCompanyAddress({
              city: detail.city || undefined,
              street,
              number,
              country: detail.country || 'Polska',
            });
          }
        }
      } catch (error) {
        console.error('Failed to load vehicles:', error);
        toast.error(t('errors.loadingFailed'));
      }
    }
    load();
  }, [companyNip, getVehicles, getCompanyDetail, companyName, onCompanyNameLoad]);

  const handleVehiclesChanged = async () => {
    const data = await getVehicles(companyNip);
    setVehicles(data);
    if (data.length > 0 && !data.find(v => v.id === selectedVehicleId)) {
      setSelectedVehicleId(data[0].id);
    }
  };

  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);

  return (
    <div className="flex flex-col gap-4">
      {vehicles.length > 1 && activeTab === 'monthly' && (
        <HeaderActions>
          <Select value={selectedVehicleId} onValueChange={setSelectedVehicleId}>
            <SelectTrigger size="sm" className="w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {vehicles.map(v => (
                <SelectItem key={v.id} value={v.id}>
                  {v.displayName} ({v.registrationNumber})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </HeaderActions>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          setVisitedTabs(prev => (prev.has(v) ? prev : new Set(prev).add(v)));
        }}
      >
        <TabsList>
          <TabsTrigger value="vehicles">{t('mileage.tabs.vehicles')}</TabsTrigger>
          <TabsTrigger value="routes">{t('mileage.tabs.routes')}</TabsTrigger>
          <TabsTrigger value="places">Places</TabsTrigger>
          <TabsTrigger value="monthly" className="gap-1.5">
            {t('mileage.tabs.monthly')}
            {selectedVehicle && (
              <Badge variant="secondary">{selectedVehicle.registrationNumber}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* forceMount + hidden-when-inactive: preserve lists/forms/drafts across tab switches, */}
        {/* but only render children once a tab has been visited (lazy first mount, kept mounted after) */}
        <TabsContent value="vehicles" forceMount className="pt-2 data-[state=inactive]:hidden">
          {visitedTabs.has('vehicles') && (
            <VehicleManagement
              companyNip={companyNip}
              vehicles={vehicles}
              onVehiclesChanged={handleVehiclesChanged}
            />
          )}
        </TabsContent>

        <TabsContent value="routes" forceMount className="pt-2 data-[state=inactive]:hidden">
          {visitedTabs.has('routes') && (
            <StandardRouteManagement
              companyNip={companyNip}
              vehicles={vehicles}
              companyAddress={companyAddress}
            />
          )}
        </TabsContent>

        <TabsContent value="places" forceMount className="pt-2 data-[state=inactive]:hidden">
          {visitedTabs.has('places') && (
            selectedVehicleId ? (
              <MileagePlacesPanel
                companyNip={companyNip}
                vehicleId={selectedVehicleId}
              />
            ) : (
              <p className="text-muted-foreground text-sm">{t('mileage.vehicles.noVehicles')}</p>
            )
          )}
        </TabsContent>

        <TabsContent value="monthly" forceMount className="pt-2 data-[state=inactive]:hidden">
          {visitedTabs.has('monthly') && (
            selectedVehicleId ? (
              <MonthlyMileageView
                companyNip={companyNip}
                vehicleId={selectedVehicleId}
                vehicles={vehicles}
                companyAddress={companyAddress}
              />
            ) : (
              <p className="text-muted-foreground text-sm">{t('mileage.vehicles.noVehicles')}</p>
            )
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
